import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { Input, Dropdown, DropdownItem } from '@grupoboticario/flora-react';
import { ReactSelectFlora } from 'components/ReactSelectFlora';
import Calendar from 'react-calendar';

import { useDebounce } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { Filters as IFilters } from 'react-table';
import {
  getClaimStatusAction,
  getStatesAction,
} from 'store/modules/claims/actions';
import { IClaim, States, Status } from 'store/modules/claims/types';
import { STATUS_OBJ } from 'utils/statusEnum';
import { getCoverageList } from 'store/modules/coverage/actions';
import { ApplicationState } from 'store/store.inteface';
import { statusList, movementSearchList } from 'utils/statusList';
import { ROLES } from 'config/roles';
import {
  FiltersContainer,
  CleanFiltersButton,
  CalendarIcon,
  CalendarInput,
  CalendarRef,
} from './styles';
import { CalendarWrap } from '../stylesCalendar';

interface FiltersProps {
  values: IFilters<IClaim>;
  setValues: Dispatch<SetStateAction<IFilters<IClaim>>>;
  cleanFilters: any;
  datesValue: Date;
  setDatesValue: any;
  groupedStatusValue: any;
  setGroupedStatusValue: any;
}

interface SelectFields {
  label: string;
  id: string;
  options: Status[] | States[];
}

interface OnChangeFilter {
  id: string;
  value: any;
}

interface SelectOptions {
  label: string;
  value?: number | null;
  data?: any;
  options?: any;
}

export const Filters = ({
  values,
  setValues,
  cleanFilters,
  datesValue,
  setDatesValue,
  groupedStatusValue,
  setGroupedStatusValue,
}: FiltersProps) => {
  const [valueInput, setValueInput] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);

  const { status, states } = useSelector(
    (state: ApplicationState) => state.claims,
  );
  const { coverageList } = useSelector(
    (state: ApplicationState) => state.coverage,
  );
  const { stores } = useSelector(
    (state: ApplicationState) => state.storesContext,
  );
  const { rolesList } = useSelector((state: ApplicationState) => state.roles);

  const [mountedCoverageList, setMountedCoverageList] = useState<
    SelectOptions[]
  >([]);

  const mountedStatusList: any = [
    { label: 'Nenhum', value: '' },
    { label: 'Status', options: movementSearchList },
    { label: 'Status Corretora', options: statusList },
  ];

  const dispatch = useDispatch();

  const ref = useRef<any>();

  const formatDate = (date: any) => {
    const newDate = new Date(date);
    const day = newDate.toLocaleString('pt-BR', { day: '2-digit' });
    const month = newDate.toLocaleString('pt-BR', { month: '2-digit' });
    const year = newDate.toLocaleString('pt-BR', { year: 'numeric' });
    const newDateString = `${day}/${month}/${year}`;
    return newDateString;
  };

  function onChangeFilter({ id, value }: OnChangeFilter) {
    const elementIndex = values.findIndex(element => element.id === id);
    const newValues = [...values];
    const newFilter = {
      id,
      value,
    };
    if (elementIndex >= 0) {
      newValues[elementIndex] = newFilter;
    } else {
      newValues.push(newFilter);
    }

    setValues(newValues);
  }

  const onChangeCalendar = (date: any) => {
    setDatesValue(date);
  };

  const debounceFilter = useDebounce(onChangeFilter, 500);

  useEffect(() => {
    if (stores.length > 0 || rolesList[0] === ROLES.MANAGEMENT) {
      dispatch(getStatesAction());
      dispatch(getClaimStatusAction());
      dispatch(getCoverageList());
    }
  }, [dispatch, stores, rolesList]);

  const inputs = useMemo(
    () => [
      { label: 'Número do Sinistro', id: 'number' },
      { label: 'CP', id: 'cp' },
      { label: 'Código/Loja', id: 'franchise' },
    ],
    [],
  );
  const selects = useMemo<SelectFields[]>(
    () => [
      { label: 'Estado', id: 'uf', options: states },
      {
        label: 'Status',
        id: 'status',
        options: status.filter(opt => opt.name !== STATUS_OBJ.Draft),
      },
    ],
    [status, states],
  );

  const getCoverageValue = useCallback(() => {
    const coverageValue = values.find(t => t.id === 'coverage')?.value;
    if (!coverageValue) {
      return null;
    }
    let findCoverage: any = null;
    mountedCoverageList.forEach(coverage => {
      const checkOptions = coverage?.options?.find(
        (subcoverage: any) => subcoverage?.value === coverageValue,
      );
      if (checkOptions) {
        findCoverage = checkOptions;
      }
    });
    return findCoverage;
  }, [values, mountedCoverageList]);

  useEffect(() => {
    if (coverageList.length > 0 && mountedCoverageList.length === 0) {
      const mountedList: SelectOptions[] = coverageList.map(coverage => ({
        label: coverage.name,
        data: undefined,
        options: coverage.subCoverage.map(subCoverage => ({
          label: subCoverage.name,
          value: subCoverage.name,
          data: subCoverage,
        })),
      }));
      mountedList.unshift({
        label: 'Nenhum',
        value: undefined,
      });
      setMountedCoverageList(mountedList);
    }
  }, [coverageList, dispatch, mountedCoverageList]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (mountedCoverageList.length <= 0) return;

    if (queryParams.get('id')) {
      let findCoverage: any = null;
      mountedCoverageList.forEach(coverage => {
        findCoverage = coverage?.options?.find(
          (subcoverage: any) => subcoverage?.data?.id === queryParams.get('id'),
        );
        if (!findCoverage?.value) return;
        onChangeFilter({ id: 'coverage', value: findCoverage.value });
      });
    }
    if (queryParams.get('status')) {
      let queryStatus = mountedStatusList[0];
      if (!queryParams.get('status')) return;
      mountedStatusList.forEach((group: any) => {
        const findStatus = group?.options?.find(
          (groupStatus: any) =>
            groupStatus?.value === queryParams.get('status'),
        );
        if (findStatus) {
          queryStatus = findStatus;
        }
      });
      setGroupedStatusValue(queryStatus);
    }
    if (queryParams.get('endDate') && queryParams.get('startDate')) {
      const startDate: string = queryParams.get('startDate') || '';
      const endDate: string = queryParams.get('endDate') || '';
      setDatesValue([
        new Date(`${startDate}T00:00:00`),
        new Date(`${endDate}T00:00:00`),
      ]);
    }
  }, [mountedCoverageList]);

  useEffect(() => {
    if (datesValue[0] && datesValue[1]) {
      setValueInput(
        `${formatDate(datesValue[0])} à ${formatDate(datesValue[1])}`,
      );
    } else {
      setValueInput('');
    }
  }, [datesValue]);

  useEffect(() => {
    const listener = (event: TouchEvent | MouseEvent) => {
      if (!ref.current || ref.current?.contains(event.target)) {
        return;
      }
      setShowCalendar(false);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref]);

  return (
    <FiltersContainer>
      {inputs.map(input => (
        <Input
          key={input.id}
          label={input.label}
          // value={values.find(t => t.id === input.id)?.value}
          placeholder=""
          id={input.id}
          onChange={({ target }) => debounceFilter(target)}
        />
      ))}
      <CalendarRef ref={ref}>
        <CalendarInput onClick={() => setShowCalendar(!showCalendar)}>
          <span>Período:</span>
          <input
            id="inputDate"
            value={valueInput}
            onChange={() => null}
            disabled
          />
          <CalendarIcon />
        </CalendarInput>

        {showCalendar && (
          <CalendarWrap>
            <Calendar
              onChange={(date: any) => onChangeCalendar(date)}
              value={datesValue}
              selectRange
            />
          </CalendarWrap>
        )}
      </CalendarRef>

      <ReactSelectFlora
        label="Cobertura"
        type="coverage"
        options={mountedCoverageList}
        value={getCoverageValue()}
        onChangeSelect={
          (type: string, { value }: any) => onChangeFilter({ id: type, value })
          // eslint-disable-next-line react/jsx-curly-newline
        }
      />
      <Dropdown
        id={selects[0].id}
        labelText={selects[0].label}
        optionalText=""
        value={values.find(t => t.id === selects[0].id)?.value}
        onChange={
          ({ target }) =>
            onChangeFilter({ id: selects[0].id, value: target.value })
          // eslint-disable-next-line react/jsx-curly-newline
        }
      >
        {/* <DropdownItem value="">Nenhum</DropdownItem> */}
        {selects[0].options.map((option: any) => (
          <DropdownItem key={option.id} value={option.name}>
            {option.name}
          </DropdownItem>
        ))}
      </Dropdown>
      <ReactSelectFlora
        label="Status"
        options={mountedStatusList}
        value={groupedStatusValue}
        onChangeSelect={(e: any) => setGroupedStatusValue(e)}
      />
      <CleanFiltersButton onClick={() => cleanFilters()}>
        Limpar Filtros
      </CleanFiltersButton>
    </FiltersContainer>
  );
};
